import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  Avatar,
  Divider
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Schedule as PendingIcon,
  Assignment as TimesheetIcon,
  TrendingUp as OvertimeIcon,
  Group as TeamIcon,
  Warning as WarningIcon,
  Assessment as ReportsIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';

interface TimeEntry {
  id: number;
  employee_id: number;
  employee_name: string;
  employee_number: string;
  department: string;
  project_name?: string;
  task_description?: string;
  clock_in_time: string;
  clock_out_time?: string;
  total_hours?: number;
  regular_hours?: number;
  overtime_hours?: number;
  location?: string;
  notes?: string;
  approved: boolean;
  status: 'completed' | 'approved' | 'rejected';
  submitted_at: string;
}

interface WeeklyTimesheet {
  employee_id: number;
  employee_name: string;
  employee_number: string;
  department: string;
  week_start: string;
  week_end: string;
  total_hours: number;
  regular_hours: number;
  overtime_hours: number;
  entries_count: number;
  approval_status: 'pending' | 'approved' | 'rejected';
  submitted_at: string;
}

const TimeApprovalDashboard: React.FC = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [pendingEntries, setPendingEntries] = useState<TimeEntry[]>([]);
  const [weeklyTimesheets, setWeeklyTimesheets] = useState<WeeklyTimesheet[]>([]);
  const [selectedEntries, setSelectedEntries] = useState<number[]>([]);
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [approvalNotes, setApprovalNotes] = useState('');

  useEffect(() => {
    fetchPendingEntries();
    fetchWeeklyTimesheets();
  }, []);

  const fetchPendingEntries = async () => {
    // Mock data for demonstration
    setPendingEntries([
      {
        id: 1,
        employee_id: 1,
        employee_name: 'Sarah Johnson',
        employee_number: 'EMP001',
        department: 'Sales',
        project_name: 'Website Redesign',
        task_description: 'Client meeting and requirements gathering',
        clock_in_time: '2024-01-15T09:00:00',
        clock_out_time: '2024-01-15T18:30:00',
        total_hours: 8.5,
        regular_hours: 8.0,
        overtime_hours: 0.5,
        location: 'client_site',
        notes: 'Extended meeting with client stakeholders',
        approved: false,
        status: 'completed',
        submitted_at: '2024-01-15T18:35:00'
      },
      {
        id: 2,
        employee_id: 2,
        employee_name: 'James Wilson',
        employee_number: 'EMP002',
        department: 'Warehouse',
        project_name: 'Inventory Management',
        task_description: 'Stock counting and organization',
        clock_in_time: '2024-01-15T07:00:00',
        clock_out_time: '2024-01-15T16:00:00',
        total_hours: 8.0,
        regular_hours: 8.0,
        overtime_hours: 0,
        location: 'warehouse',
        notes: 'Completed quarterly inventory count',
        approved: false,
        status: 'completed',
        submitted_at: '2024-01-15T16:05:00'
      },
      {
        id: 3,
        employee_id: 3,
        employee_name: 'Emma Davis',
        employee_number: 'EMP003',
        department: 'IT',
        project_name: 'System Maintenance',
        task_description: 'Server updates and security patches',
        clock_in_time: '2024-01-15T08:30:00',
        clock_out_time: '2024-01-15T19:00:00',
        total_hours: 9.5,
        regular_hours: 8.0,
        overtime_hours: 1.5,
        location: 'office',
        notes: 'Critical security updates required overtime',
        approved: false,
        status: 'completed',
        submitted_at: '2024-01-15T19:05:00'
      }
    ]);
  };

  const fetchWeeklyTimesheets = async () => {
    setWeeklyTimesheets([
      {
        employee_id: 1,
        employee_name: 'Sarah Johnson',
        employee_number: 'EMP001',
        department: 'Sales',
        week_start: '2024-01-08',
        week_end: '2024-01-14',
        total_hours: 42.5,
        regular_hours: 40.0,
        overtime_hours: 2.5,
        entries_count: 5,
        approval_status: 'pending',
        submitted_at: '2024-01-14T17:00:00'
      },
      {
        employee_id: 2,
        employee_name: 'James Wilson',
        employee_number: 'EMP002',
        department: 'Warehouse',
        week_start: '2024-01-08',
        week_end: '2024-01-14',
        total_hours: 40.0,
        regular_hours: 40.0,
        overtime_hours: 0,
        entries_count: 5,
        approval_status: 'approved',
        submitted_at: '2024-01-14T16:30:00'
      }
    ]);
  };

  const handleSelectEntry = (entryId: number) => {
    setSelectedEntries(prev => 
      prev.includes(entryId) 
        ? prev.filter(id => id !== entryId)
        : [...prev, entryId]
    );
  };

  const handleSelectAll = () => {
    const filteredEntries = getFilteredEntries();
    if (selectedEntries.length === filteredEntries.length) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries(filteredEntries.map(entry => entry.id));
    }
  };

  const handleBulkApproval = (action: 'approve' | 'reject') => {
    setApprovalAction(action);
    setApprovalDialogOpen(true);
  };

  const confirmBulkApproval = async () => {
    // Update selected entries
    setPendingEntries(prev => prev.map(entry => 
      selectedEntries.includes(entry.id) 
        ? { ...entry, approved: approvalAction === 'approve', status: approvalAction === 'approve' ? 'approved' : 'rejected' }
        : entry
    ));

    // Clear selections and close dialog
    setSelectedEntries([]);
    setApprovalDialogOpen(false);
    setApprovalNotes('');
  };

  const getFilteredEntries = () => {
    return pendingEntries.filter(entry => 
      filterDepartment === 'all' || entry.department === filterDepartment
    );
  };

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (timeString: string) => {
    return new Date(timeString).toLocaleDateString('en-US');
  };

  const formatHours = (hours?: number) => {
    if (!hours) return '0.0h';
    return `${hours.toFixed(1)}h`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const departments = ['all', 'Sales', 'Warehouse', 'IT', 'Finance', 'HR', 'Operations'];
  const filteredEntries = getFilteredEntries();
  const pendingCount = pendingEntries.filter(e => e.status === 'completed').length;
  const overtimeCount = pendingEntries.filter(e => e.overtime_hours && e.overtime_hours > 0).length;
  const totalOvertimeHours = pendingEntries.reduce((sum, e) => sum + (e.overtime_hours || 0), 0);

  const TabPanel = ({ children, value, index }: any) => (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );

  return (
    <Box sx={{ p: 3, pt: 2 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4,
        mt: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            Time Approval Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review and approve employee time entries and timesheets
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button startIcon={<ReportsIcon />} variant="outlined">
            Generate Report
          </Button>
          {selectedEntries.length > 0 && (
            <>
              <Button 
                startIcon={<ApproveIcon />} 
                variant="contained" 
                color="success"
                onClick={() => handleBulkApproval('approve')}
              >
                Approve ({selectedEntries.length})
              </Button>
              <Button 
                startIcon={<RejectIcon />} 
                variant="contained" 
                color="error"
                onClick={() => handleBulkApproval('reject')}
              >
                Reject ({selectedEntries.length})
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PendingIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {pendingCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pending Approvals
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <OvertimeIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="error.main">
                {overtimeCount}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Overtime Entries
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TimesheetIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="info.main">
                {formatHours(totalOvertimeHours)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Overtime Hours
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <TeamIcon color="success" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {weeklyTimesheets.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Weekly Timesheets
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Overtime Alert */}
      {overtimeCount > 0 && (
        <Alert severity="warning" sx={{ mb: 4 }}>
          <Typography variant="subtitle2" gutterBottom>
            ⚠️ Overtime Entries Require Attention
          </Typography>
          <Typography variant="body2">
            {overtimeCount} employees have logged overtime hours totaling {formatHours(totalOvertimeHours)}. 
            Please review these entries carefully as they will impact payroll costs.
          </Typography>
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={selectedTab} onChange={(e, newValue) => setSelectedTab(newValue)}>
          <Tab label={`Daily Entries (${pendingCount})`} />
          <Tab label={`Weekly Timesheets (${weeklyTimesheets.filter(t => t.approval_status === 'pending').length})`} />
        </Tabs>
      </Box>

      {/* Daily Entries Tab */}
      <TabPanel value={selectedTab} index={0}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Pending Time Entries</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Department</InputLabel>
                  <Select
                    value={filterDepartment}
                    onChange={(e) => setFilterDepartment(e.target.value)}
                  >
                    {departments.map(dept => (
                      <MenuItem key={dept} value={dept}>
                        {dept === 'all' ? 'All Departments' : dept}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  startIcon={<Checkbox checked={selectedEntries.length === filteredEntries.length && filteredEntries.length > 0} />}
                  onClick={handleSelectAll}
                  size="small"
                >
                  Select All
                </Button>
              </Box>
            </Box>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox"></TableCell>
                    <TableCell>Employee</TableCell>
                    <TableCell>Date & Time</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell align="right">Hours</TableCell>
                    <TableCell align="right">Overtime</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEntries.map((entry) => (
                    <TableRow key={entry.id} selected={selectedEntries.includes(entry.id)}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedEntries.includes(entry.id)}
                          onChange={() => handleSelectEntry(entry.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {entry.employee_name.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {entry.employee_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {entry.employee_number} • {entry.department}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(entry.clock_in_time)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatTime(entry.clock_in_time)} - {entry.clock_out_time ? formatTime(entry.clock_out_time) : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="subtitle2">
                            {entry.project_name || 'No Project'}
                          </Typography>
                          {entry.task_description && (
                            <Typography variant="caption" color="text.secondary">
                              {entry.task_description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2">
                          {formatHours(entry.total_hours)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          color={entry.overtime_hours && entry.overtime_hours > 0 ? 'error.main' : 'text.secondary'}
                          sx={{ fontWeight: entry.overtime_hours && entry.overtime_hours > 0 ? 600 : 400 }}
                        >
                          {formatHours(entry.overtime_hours)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={entry.location ? (entry.location.charAt(0).toUpperCase() + entry.location.slice(1)) : 'Office'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" startIcon={<ApproveIcon />} color="success">
                            Approve
                          </Button>
                          <Button size="small" startIcon={<RejectIcon />} color="error">
                            Reject
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Weekly Timesheets Tab */}
      <TabPanel value={selectedTab} index={1}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Weekly Timesheets</Typography>
            
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Employee</TableCell>
                    <TableCell>Week Period</TableCell>
                    <TableCell align="right">Total Hours</TableCell>
                    <TableCell align="right">Overtime</TableCell>
                    <TableCell align="right">Entries</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklyTimesheets.map((timesheet) => (
                    <TableRow key={`${timesheet.employee_id}-${timesheet.week_start}`}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ width: 32, height: 32 }}>
                            {timesheet.employee_name.split(' ').map(n => n[0]).join('')}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2">
                              {timesheet.employee_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {timesheet.employee_number} • {timesheet.department}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(timesheet.week_start)} - {formatDate(timesheet.week_end)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="subtitle2">
                          {formatHours(timesheet.total_hours)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body2" 
                          color={timesheet.overtime_hours > 0 ? 'error.main' : 'text.secondary'}
                          sx={{ fontWeight: timesheet.overtime_hours > 0 ? 600 : 400 }}
                        >
                          {formatHours(timesheet.overtime_hours)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {timesheet.entries_count}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={timesheet.approval_status.charAt(0).toUpperCase() + timesheet.approval_status.slice(1)}
                          color={getStatusColor(timesheet.approval_status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button size="small" startIcon={<ViewIcon />}>
                            View
                          </Button>
                          {timesheet.approval_status === 'pending' && (
                            <>
                              <Button size="small" startIcon={<ApproveIcon />} color="success">
                                Approve
                              </Button>
                              <Button size="small" startIcon={<RejectIcon />} color="error">
                                Reject
                              </Button>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </TabPanel>

      {/* Bulk Approval Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {approvalAction === 'approve' ? 'Approve Time Entries' : 'Reject Time Entries'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to {approvalAction} {selectedEntries.length} time entries?
          </Typography>
          
          <TextField
            fullWidth
            label="Notes (Optional)"
            multiline
            rows={3}
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            placeholder={`Add notes for this ${approvalAction} action...`}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            color={approvalAction === 'approve' ? 'success' : 'error'}
            onClick={confirmBulkApproval}
          >
            {approvalAction === 'approve' ? 'Approve' : 'Reject'} Entries
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeApprovalDashboard;
